import React, { useMemo } from 'react';
import { GridItem, TextField, GridContainer, Button, Autocomplete } from '../../../components/shared';
import {
  Typography,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Chip,
  Tooltip,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { WarningAmberOutlined } from '@mui/icons-material';
import { meterUpdateSchema } from '../../../schema/validationSchemas';
import {
  useGetModemsByMachineIdQuery,
  useUpdateMachineMeterByIdMutation,
} from '../../../services/Machines/MachinesService';
import { useFormik } from 'formik';
import useStyles from './style';
import { DENOMINATION, METER_TYPES, TYPE } from '../../../constants/MeterConstants';
import { useParams } from 'react-router-dom';
import { getMachineAcceptedPaymentType } from '../../../utils/collection-methods';
import { getModemOptions, formatCoinCost } from './utils';
import { LEASE_COIN_COST_UNIT_OF_MEASURES } from '../../../constants/rentDetailsConfig';

const EditMeterFields = ({ setIsUpdateMeterField, currentMeter }) => {
  const classes = useStyles();
  const params = useParams();
  const [updateMachineMeterApi] = useUpdateMachineMeterByIdMutation();
  let status = currentMeter?.status;
  const { currentData: currentModemsData, isSuccess: modemSuccess } = useGetModemsByMachineIdQuery(Number(params.id));

  const modemOptions = useMemo(
    () => getModemOptions(currentModemsData?.data?.modems),
    [currentModemsData, modemSuccess],
  );

  const formik = useFormik({
    initialValues: {
      meterName: currentMeter?.meterName,
      meterPosition: currentMeter?.identifier.trim() || '',
      maxCount: currentMeter?.maxCount,
      description: currentMeter?.description,
      displayName: currentMeter?.displayName || '',
      coinCost: currentMeter?.assetMeterCoinCost ?? '',
      coinCostUom:
        currentMeter?.assetMeterCoinCostUom ||
        currentMeter?.leaseCoinCostUom ||
        LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR,
      denomination: DENOMINATION.filter((item) => item.text === currentMeter?.denomination)?.[0] || null,
      tokenPayout: currentMeter?.tokenPayout || '',
      machineAcceptedPaymentType:
        TYPE.filter((item) => item.text === getMachineAcceptedPaymentType(currentMeter))?.[0] || null,
      modemId: {
        text: currentMeter?.modem?.serialNumber || '',
        value: currentMeter?.modem?.id || '',
      },
      currentAssetMeterReading: currentMeter?.currentAssetMeterReading || 0,
    },
    validationSchema: meterUpdateSchema(),
    onSubmit: (data) => {
      let apiData = {};
      switch (currentMeter?.meterName) {
        case METER_TYPES.IMPULSE_READER_METER:
          apiData = {
            id: currentMeter?.id,
            description: data.description?.trim() || '',
          };
          break;
        default:
          apiData = {
            id: currentMeter?.id,
            identifier: data.meterPosition.trim() || null,
            maxCount: data.maxCount || null,
            description: data.description?.trim() || '',
            displayName: data.displayName?.trim() || '',
            coinCost: data.coinCost ? parseFloat(data.coinCost) : null,
            coinCostUom: data.coinCost ? data.coinCostUom || LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR : null,
            denomination: data.denomination?.text,
            tokenPayout: data.tokenPayout,
            machineAcceptedPaymentType: data.machineAcceptedPaymentType?.text,
            modemId: data?.modemId?.value || null,
            currentAssetMeterReading: data.currentAssetMeterReading ?? 0,
          };
          break;
      }
      updateMachineMeterApi(apiData).then((response) => {
        if (response.data) {
          setIsUpdateMeterField(false);
        }
      });
    },
  });

  const onCancelHandler = () => {
    setIsUpdateMeterField(false);
  };

  const archiveHandler = () => {
    updateMachineMeterApi({
      id: currentMeter?.id,
      status: !currentMeter?.status,
    }).then((response) => {
      if (response.data) {
        setIsUpdateMeterField(false);
      }
    });
  };

  const { values, errors, touched, getFieldProps, handleSubmit, setFieldValue, setFieldTouched } = formik;
  const { meterName } = currentMeter;
  const isImpulseReaderMeter = meterName === METER_TYPES.IMPULSE_READER_METER;
  const coinCostUom = values.coinCostUom || currentMeter?.leaseCoinCostUom || LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR;

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader title={<Typography variant="h2">Edit Meter</Typography>} className={classes.addMeterHead} />
        <CardContent className={classes.meterCardContent}>
          <GridContainer spacing={2} alignItems="center">
            <GridItem xs={12} md={12}>
              <Typography variant="h5">{meterName}</Typography>
            </GridItem>
            {isImpulseReaderMeter ? (
              <>
                <GridItem xs={12}>
                  <TextField
                    label="Description"
                    {...getFieldProps('description')}
                    InputProps={{
                      'data-testid': 'editMeterFields',
                    }}
                  />
                </GridItem>
              </>
            ) : (
              <>
                <GridItem xs={12} md={4}>
                  <TextField
                    label="Meter Position"
                    {...getFieldProps('meterPosition')}
                    inputProps={{
                      'data-testid': 'editMeterFields',
                    }}
                  />
                </GridItem>
                <GridItem xs={12} md={4}>
                  <TextField
                    label="Current Meter"
                    {...getFieldProps('currentAssetMeterReading')}
                    inputProps={{
                      'data-testid': 'editMeterFields',
                    }}
                  />
                </GridItem>
                <GridItem xs={12} md={4}>
                  <TextField
                    label="Max Count"
                    {...getFieldProps('maxCount')}
                    error={Boolean(touched.maxCount && errors.maxCount)}
                    helperText={touched.maxCount && errors.maxCount}
                    inputProps={{
                      maxLength: 6,
                      'data-testid': 'editMeterFields',
                    }}
                  />
                </GridItem>
                {meterName === METER_TYPES.PRIZE_METER && (
                  <GridItem xs={12}>
                    <TextField
                      label="Description"
                      {...getFieldProps('description')}
                      InputProps={{
                        'data-testid': 'editMeterFields',
                      }}
                    />
                  </GridItem>
                )}
                {meterName === METER_TYPES.MEDALLION_METER && (
                  <>
                    <GridItem xs={12} md={6}>
                      <TextField
                        sx={{
                          mt:
                            currentMeter?.assetMeterCoinCost != null || currentMeter?.coinCostDiffersFromDefault
                              ? '24px'
                              : undefined,
                        }}
                        label="Display Name"
                        {...getFieldProps('displayName')}
                        InputProps={{
                          'data-testid': 'editMeterFields',
                        }}
                      />
                    </GridItem>
                    <GridItem xs={12} md={6}>
                      {(currentMeter?.assetMeterCoinCost != null || currentMeter?.coinCostDiffersFromDefault) && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}>
                          {currentMeter?.assetMeterCoinCost != null && (
                            <Tooltip title="Not linked to lease default" arrow>
                              <Chip
                                label="Overridden"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          )}
                          {currentMeter?.coinCostDiffersFromDefault && (
                            <Tooltip
                              title={`Differs from lease default of ${formatCoinCost(
                                currentMeter?.leaseCoinCost,
                                currentMeter?.leaseCoinCostUom,
                              )}`}
                              arrow
                            >
                              <Chip
                                icon={<WarningAmberOutlined fontSize="small" />}
                                label="Differs from default"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box flexGrow={1} minWidth={0}>
                          <TextField
                            label="Coin Cost"
                            type="number"
                            {...getFieldProps('coinCost')}
                            onChange={(event) => {
                              const { value } = event.target;
                              setFieldValue('coinCost', value);
                              if (value === '') {
                                setFieldValue('coinCostUom', '');
                              }
                            }}
                            placeholder={
                              values.coinCost === '' && currentMeter?.leaseCoinCost != null
                                ? `${currentMeter.leaseCoinCost}`
                                : undefined
                            }
                            InputProps={{
                              'data-testid': 'editMeterFields',
                              startAdornment:
                                coinCostUom !== LEASE_COIN_COST_UNIT_OF_MEASURES.PERCENTAGE ? (
                                  <span style={{ marginRight: '4px' }}>$</span>
                                ) : undefined,
                              endAdornment:
                                coinCostUom === LEASE_COIN_COST_UNIT_OF_MEASURES.PERCENTAGE ? (
                                  <span style={{ marginLeft: '4px' }}>%</span>
                                ) : undefined,
                            }}
                            inputProps={{
                              step: '0.01',
                              min: '0',
                            }}
                            error={Boolean(touched.coinCost && errors.coinCost)}
                            helperText={touched.coinCost && errors.coinCost}
                          />
                        </Box>
                        <Box flexShrink={0}>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            color="primary"
                            data-testid="coinCostUomToggle"
                            value={coinCostUom}
                            onChange={(event, newValue) => {
                              if (newValue) {
                                setFieldValue('coinCostUom', newValue);
                              }
                            }}
                          >
                            <ToggleButton value={LEASE_COIN_COST_UNIT_OF_MEASURES.DOLLAR} aria-label="Dollar">
                              $
                            </ToggleButton>
                            <ToggleButton value={LEASE_COIN_COST_UNIT_OF_MEASURES.PERCENTAGE} aria-label="Percentage">
                              %
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      </Box>
                    </GridItem>
                  </>
                )}
                {[METER_TYPES.CASH_METER, METER_TYPES.CREDIT_METER, METER_TYPES.TOKEN_CHANGER_METER].includes(
                  meterName,
                ) && (
                  <GridItem xs={12}>
                    <Autocomplete
                      label="Modem"
                      data-testid="editMeterFields"
                      value={values.modemId}
                      options={modemOptions || []}
                      getOptionLabel={(option) => option.text || ''}
                      onChange={(e, value) => {
                        setFieldValue('modemId', value);
                      }}
                      onBlur={() => setFieldTouched('modemId')}
                    />
                  </GridItem>
                )}
                {meterName === METER_TYPES.TOKEN_CHANGER_METER && (
                  <>
                    <GridItem xs={12} md={4}>
                      <Autocomplete
                        label="Denomination"
                        data-testid="form-elements"
                        value={values.denomination}
                        options={DENOMINATION}
                        getOptionLabel={(option) => option.text || ''}
                        onChange={(e, value) => {
                          setFieldValue('denomination', value);
                        }}
                        onBlur={() => setFieldTouched('denomination')}
                        error={Boolean(touched.denomination && errors.denomination)}
                        helperText={touched.denomination && errors.denomination}
                      />
                    </GridItem>
                    <GridItem xs={12} md={4}>
                      <Autocomplete
                        label="Type"
                        data-testid="form-elements"
                        value={values.machineAcceptedPaymentType}
                        options={TYPE}
                        getOptionLabel={(option) => option.text || ''}
                        onChange={(e, value) => {
                          setFieldValue('machineAcceptedPaymentType', value);
                        }}
                        onBlur={() => setFieldTouched('machineAcceptedPaymentType')}
                        error={Boolean(touched.machineAcceptedPaymentType && errors.machineAcceptedPaymentType)}
                        helperText={touched.machineAcceptedPaymentType && errors.machineAcceptedPaymentType}
                      />
                    </GridItem>
                    <GridItem xs={12} md={4}>
                      <TextField
                        label="Token Payout"
                        {...getFieldProps('tokenPayout')}
                        error={Boolean(touched.tokenPayout && errors.tokenPayout)}
                        helperText={touched.tokenPayout && errors.tokenPayout}
                        inputProps={{ maxLength: 6 }}
                        InputProps={{
                          'data-testid': 'editMeterFields',
                        }}
                      />
                    </GridItem>
                  </>
                )}
              </>
            )}
          </GridContainer>
          <GridContainer justifyContent="space-around">
            <GridItem xs={12} md={12}>
              <Stack direction={'row'} className={classes.btn}>
                <Stack direction={'row'}>
                  <Button type="Submit" variant="contained">
                    <Typography variant="button">UPDATE</Typography>
                  </Button>
                  <Button onClick={onCancelHandler}>
                    <Typography variant="button">CANCEL</Typography>
                  </Button>
                </Stack>
                <Button color={status ? 'error' : 'success'} className={classes.errorButton} onClick={archiveHandler}>
                  {status ? 'ARCHIVE METER' : 'ACTIVATE METER'}
                </Button>
              </Stack>
            </GridItem>
          </GridContainer>
        </CardContent>
      </Card>
    </form>
  );
};

export default EditMeterFields;
